import { X, ExternalLink, CreditCard } from 'lucide-react';
import { getStripeCustomerUrl, getStripeSubscriptionUrl } from '../lib/admin';

interface SubscriptionManagementProps {
  userEmail: string;
  subscriptionId?: string;
  customerId?: string;
  subscriptionStatus: string;
  onClose: () => void;
}

export function SubscriptionManagement({
  userEmail,
  subscriptionId,
  customerId,
  subscriptionStatus,
  onClose,
}: SubscriptionManagementProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-scale-navy rounded-lg shadow-xl max-w-2xl w-full mx-4 border border-gray-800">
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">Subscription Management</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">User Information</h3>
            <div className="bg-scale-darker p-4 rounded-lg space-y-2">
              <div>
                <span className="text-gray-400">Email:</span>
                <span className="text-white ml-2">{userEmail}</span>
              </div>
              <div>
                <span className="text-gray-400">Status:</span>
                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  subscriptionStatus === 'active' ? 'bg-green-500/10 text-green-400' :
                  subscriptionStatus === 'pending' ? 'bg-yellow-500/10 text-yellow-400' :
                  subscriptionStatus === 'canceled' ? 'bg-red-500/10 text-red-400' :
                  'bg-gray-500/10 text-gray-400'
                }`}>
                  {subscriptionStatus || 'No Subscription'}
                </span>
              </div>
            </div>
          </div>

          {subscriptionId && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Subscription Details</h3>
              <div className="bg-scale-darker p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-gray-400 block text-sm">Subscription ID</span>
                    <span className="text-white text-sm font-mono">{subscriptionId.substring(0, 30)}...</span>
                  </div>
                  <a
                    href={getStripeSubscriptionUrl(subscriptionId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center px-3 py-2 bg-scale-blue/10 text-scale-blue rounded-md hover:bg-scale-blue/20 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View in Stripe
                  </a>
                </div>

                {customerId && (
                  <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                    <div>
                      <span className="text-gray-400 block text-sm">Customer ID</span>
                      <span className="text-white text-sm font-mono">{customerId.substring(0, 30)}...</span>
                    </div>
                    <a
                      href={getStripeCustomerUrl(customerId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center px-3 py-2 bg-scale-blue/10 text-scale-blue rounded-md hover:bg-scale-blue/20 transition-colors"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      View Customer
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {subscriptionStatus === 'active' && subscriptionId && (
            <div className="bg-scale-blue/10 border border-scale-blue/20 rounded-lg p-4">
              <p className="text-gray-200 text-sm">
                Billing changes, including cancellation, must be completed in Stripe. The verified Stripe webhook then updates access in ScaleSmall.
              </p>
            </div>
          )}

          {subscriptionStatus === 'canceled' && (
            <div className="bg-gray-500/10 border border-gray-500/20 rounded-lg p-4">
              <p className="text-gray-400 text-center">
                This subscription has already been canceled.
              </p>
            </div>
          )}

          {!subscriptionId && (
            <div className="bg-gray-500/10 border border-gray-500/20 rounded-lg p-4">
              <p className="text-gray-400 text-center">
                This user does not have an active subscription.
              </p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-800 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
